/*
  R2R Grow Tools — shared runtime.

  Every tool in /pages/*-calculator loads this once. It carries the four things
  every tool needs and nothing else: URL state so a result can be shared, a
  sessionStorage handoff so a tent size entered in one tool pre-fills the next,
  GA4 events, and a debounce.

  Deliberately a plain global rather than a module: the theme has no build step
  and loads every script with defer, so an IIFE assigned to window is the same
  pattern the rest of assets/ already uses.
*/
window.R2RTools = window.R2RTools || (function () {
  'use strict';

  var HANDOFF_KEY = 'r2r-tools-state';

  /* --- URL state ------------------------------------------------------- */

  /* Reads the query string against a schema of defaults. The schema's types
     drive coercion, so a tool never has to parse its own params: a Number
     default yields a Number, a Boolean default yields a Boolean. */
  function readState(defaults) {
    var params = new URLSearchParams(window.location.search);
    var out = {};
    Object.keys(defaults).forEach(function (key) {
      var raw = params.get(key);
      var def = defaults[key];
      if (raw === null || raw === '') { out[key] = def; return; }
      if (typeof def === 'number') {
        var n = parseFloat(raw);
        out[key] = isNaN(n) ? def : n;
      } else if (typeof def === 'boolean') {
        out[key] = raw === '1' || raw === 'true';
      } else {
        out[key] = raw;
      }
    });
    return out;
  }

  /* Writes state into the address bar without adding a history entry, so the
     back button still leaves the page instead of stepping through every slider
     nudge. Values equal to the default are omitted to keep the URL short and
     shareable. Returns the absolute permalink. */
  function writeState(state, defaults) {
    var params = new URLSearchParams();
    Object.keys(state).forEach(function (key) {
      var v = state[key];
      if (defaults && v === defaults[key]) return;
      if (v === null || v === undefined || v === '') return;
      params.set(key, typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
    });
    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '');
    try { window.history.replaceState(null, '', url); } catch (e) { /* file:// etc */ }
    return window.location.origin + url;
  }

  /* --- Cross-tool handoff ---------------------------------------------- */

  /* Shared facts (tent dimensions, light wattage) that any tool may set and
     any other may read, so the ecosystem feels like one product rather than
     ten pages. sessionStorage, not localStorage: a grow plan is a single
     sitting, and stale dimensions from last month would be worse than none.
     Every access is guarded -- private windows can throw on read. */
  function saveShared(obj) {
    try {
      var cur = readShared();
      Object.keys(obj).forEach(function (k) {
        if (obj[k] !== null && obj[k] !== undefined) cur[k] = obj[k];
      });
      sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(cur));
    } catch (e) { /* storage unavailable; handoff is a nicety, not a feature */ }
  }

  function readShared() {
    try {
      return JSON.parse(sessionStorage.getItem(HANDOFF_KEY) || '{}') || {};
    } catch (e) { return {}; }
  }

  /* --- Analytics -------------------------------------------------------- */

  /* Sends a GA4 event, and mirrors tool_name into sessionStorage so it can be
     attached to the eventual purchase event. Shopify's checkout is a separate
     browsing context, so without this the revenue a tool generates cannot be
     attributed back to it.

     Two dataLayer conventions exist and they are not interchangeable:

       gtag.js  consumes arguments-style entries -- ['event', name, params].
                A plain {event: name} object is ignored outright.
       GTM      consumes {event: name, ...} objects and ignores arrays.

     This store runs gtag.js, injected by the Google Analytics app block, and
     that block defines its gtag() inside an IIFE -- so window.gtag is NOT
     global and cannot be called directly. Pushing the arguments-style array
     ourselves is the only route in.

     Both shapes are pushed so the tools keep working if the store later moves
     to GTM. The one setup that would double-count is gtag.js AND GTM together;
     if that ever happens, drop the object push. */
  function track(eventName, params) {
    var payload = params || {};
    window.dataLayer = window.dataLayer || [];

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    } else {
      window.dataLayer.push(['event', eventName, payload]);
    }
    window.dataLayer.push(Object.assign({ event: eventName }, payload));

    if (params && params.tool_name) {
      try { sessionStorage.setItem('r2r-last-tool', params.tool_name); } catch (e) {}
    }
  }

  /* --- Utilities -------------------------------------------------------- */

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function clamp(n, min, max) {
    if (isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
  }

  /* Copies text, preferring the async clipboard API and falling back to a
     hidden textarea for non-secure contexts and older Safari. */
  function copy(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:absolute;left:-9999px;top:0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); resolve(); }
      catch (e) { reject(e); }
      finally { document.body.removeChild(ta); }
    });
  }

  /* Arrow-key roving focus for a group of buttons acting as radios. Native
     radios would style badly here, so the ARIA pattern is implemented instead
     -- which means the keyboard behaviour has to be implemented too. */
  function bindRadioGroup(group, onSelect) {
    if (!group) return;
    var buttons = Array.prototype.slice.call(group.querySelectorAll('[role="radio"]'));

    function select(btn) {
      buttons.forEach(function (b) {
        var on = b === btn;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      if (onSelect) onSelect(btn.dataset.value, btn);
    }

    group.addEventListener('click', function (e) {
      var btn = e.target.closest('[role="radio"]');
      if (btn) select(btn);
    });

    group.addEventListener('keydown', function (e) {
      var i = buttons.indexOf(document.activeElement);
      if (i === -1) return;
      var next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = buttons[(i + 1) % buttons.length];
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = buttons[(i - 1 + buttons.length) % buttons.length];
      if (e.key === 'Home') next = buttons[0];
      if (e.key === 'End') next = buttons[buttons.length - 1];
      if (!next) return;
      e.preventDefault();
      next.focus();
      select(next);
    });

    return { select: select, buttons: buttons };
  }

  return {
    readState: readState,
    writeState: writeState,
    saveShared: saveShared,
    readShared: readShared,
    track: track,
    debounce: debounce,
    clamp: clamp,
    copy: copy,
    bindRadioGroup: bindRadioGroup
  };
})();
